/**
 * @file AuthPage.js
 * @description Represents the authentication page component.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoginForm from './LoginForm';
import MfaForm from './MfaForm';
import ForgotPasswordForm from './recovery/RequestCredentialsForm';
import ResetPasswordForm from './recovery/ResetPasswordForm';
import { useCompanyConfig } from './../../hooks/useCompanyConfig';
import Spinner from '../ui/spinners/Spinner';
import styles from './AuthPage.module.scss';
import { getCurrentUser } from '../../services/userService';
import { useAuthContext, useTheme } from '../../store/Contexts';
import { getConfigData } from '../../services/authService';
import { parseJwt } from '../../utils/utils';
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { getCurrentTheme } from '../../services/themeService';
import Toast from '../ui/toasts/Toast';
import { toast as toastify } from 'react-toastify';
import { isMobileDevice, isPWA } from '../../utils/deviceCheck';
import HeightScaledContent from '../ScaledContent/HeightScaledContent';
import { ROUTES } from '../../constants/routes';
import { isIndividualViewRoute } from '../../utils/individualViewRoutes';
import { fetchSecurityAttributes } from '../../services/securityRoleService';
import { fetchTenantTiers } from '../../services/systemService';
import { getText } from '../../i18n';

const AuthPage = () => {
    const { login, logout, updateMultitenantFlag, setAdminView } =
        useAuthContext();
    const themeStyles = useTheme();
    const navigate = useNavigate();
    const { isCompanyCodeReady, authData, setAuthData } = useCompanyConfig();
    const { companyCode } = authData;
    const [authError, setAuthError] = useState(null);
    const [authStage, setAuthStage] = useState('initial'); // 'initial', 'mfaRequired', 'authenticating-user', 'forgot-password', 'reset-password'
    const loggedOutToastId = useRef(null);

    const location = useLocation();

    useEffect(() => {
        if (location.pathname.includes(ROUTES.FORGOT_PASSWORD)) {
            setAuthStage('forgot-password');
        }
        if (location.pathname.includes(ROUTES.RESET_PASSWORD_PREFIX)) {
            setAuthStage('reset-password');
        }
    }, [location]);

    useEffect(() => {
        const checkAuthExpiration = () => {
            const isAuthExpired = localStorage.getItem('isAuthExpired');
            if (isAuthExpired === 'true') {
                loggedOutToastId.current = Toast.info(
                    getText('msg_8bdd4ac2e3c8'),
                    {
                        autoClose: false,
                    },
                    getText('msg_215d8533dd83'),
                );
                localStorage.removeItem('isAuthExpired');
            }
        };

        checkAuthExpiration();
    }, []);

    const getPostLoginRedirectPath = () => {
        const fromLocation = location.state?.from;

        if (!fromLocation || typeof fromLocation.pathname !== 'string') {
            return ROUTES.ROOT;
        }

        const pathname = fromLocation.pathname;
        const search =
            typeof fromLocation.search === 'string' ? fromLocation.search : '';
        const hash =
            typeof fromLocation.hash === 'string' ? fromLocation.hash : '';

        if (!pathname.startsWith('/') || pathname.startsWith('//')) {
            return ROUTES.ROOT;
        }

        if (fromLocation.pathname === ROUTES.LOGIN) {
            return ROUTES.ROOT;
        }

        return `${pathname}${search}${hash}`;
    };

    const processSuccessfulAuth = async response => {
        if (response.access_token) {
            delete response.username;
            delete response.password;
            setAuthStage('authenticating-user');
            localStorage.setItem('access_token', response.access_token);
            localStorage.removeItem('isAuthExpired');

            if (!response?.companyCode && localStorage.getItem('companyCode'))
                response.companyCode = localStorage.getItem('companyCode');

            const jwtPayload = parseJwt(response.access_token);
            const { tenant = null } = jwtPayload;
            if (!localStorage.getItem('tenantId') && tenant)
                localStorage.setItem('tenantId', tenant);
            //if (tenant) localStorage.setItem("tenantId", tenant);

            // Determine if multitenant flag is true or false by meeting requirements
            const isInMobileDevice = await isMobileDevice();
            const isInPWA = await isPWA();
            const isMultitenant = jwtPayload.ind == null;
            updateMultitenantFlag(isMultitenant);

            // Keep individual users in mobile-optimized individual view, but do not force
            // multitenant users out of admin/multitenant landing behavior.
            if ((isInPWA || isInMobileDevice) && !isMultitenant) {
                setAdminView(false);
            }

            window.dispatchEvent(new Event('storage'));

            return new Promise(resolve => {
                Promise.all([
                    getCurrentUser(),
                    getConfigData(response.companyCode),
                    getCurrentTheme(response.companyCode),
                    fetchSecurityAttributes(true, true),
                    fetchTenantTiers(),
                ])
                    .then(
                        ([
                            userResponse,
                            companyConfig = null,
                            companyTheme = null,
                            securityAttributes = [],
                            tenantTiers = [],
                        ]) => {
                            const systemConfig = {
                                securityAttributes,
                                tenantTiers,
                            };

                            login(
                                { ...userResponse, jwt: jwtPayload },
                                companyConfig,
                                companyTheme,
                                systemConfig,
                            );
                            const redirectPath = getPostLoginRedirectPath();
                            // An Individual-only destination signs the user in on the
                            // Individual side, whatever view they last used.
                            if (
                                !isMultitenant &&
                                isIndividualViewRoute(redirectPath)
                            ) {
                                setAdminView(false);
                            }

                            navigate(redirectPath, { replace: true });
                            resolve();
                        },
                    )
                    .catch(error => {
                        console.error('Login failed:', error);
                        logout(false, true);
                        setAuthStage('initial');
                        Toast.error(getText('msg_9ecf8863159e'));
                        resolve();
                    });
            });
        }
    };

    const returnToLogin = () => {
        const url = companyCode
            ? `${ROUTES.LOGIN}?companyCode=${companyCode}`
            : ROUTES.LOGIN;
        // Carries the pending destination through MFA cancels and recovery detours.
        navigate(url, { state: location.state });
        setAuthStage('initial');
    };

    /**
     * Handles authentication error.
     *
     * @param {string} errorMessage - The error message.
     * @param {boolean} isReturnToLogin - Indicates whether to return to the login form stage.
     * @returns {void}
     */
    const handleAuthError = (errorMessage, isReturnToLogin = false) => {
        if (isReturnToLogin) returnToLogin();
        setAuthError(errorMessage);
    };

    const dismissLoggedOutToast = () => {
        if (loggedOutToastId.current) {
            toastify.dismiss(loggedOutToastId.current);
            loggedOutToastId.current = null;
        }
    };

    /**
     * Handles the successful login response.
     * If MFA (Multi-Factor Authentication) is enabled, updates the authentication data and sets the authentication stage to "mfaRequired".
     * Otherwise, processes the successful authentication.
     * @param {Object} data - The login response data.
     */
    const handleLoginSuccess = data => {
        if (data.companyCode) {
            setAuthData(prevAuthData => ({
                ...prevAuthData,
                companyCode: data.companyCode,
            }));
            window.localStorage.setItem('companyCode', data.companyCode);
            window.localStorage.setItem('authCompanyCode', data.companyCode);
        }
        if (data.error && data.mfa && data.mfa.methods) {
            setAuthData(prevAuthData => ({
                ...prevAuthData,
                username: data.username,
                password: data.password,
                mfaMethods: data.mfa,
                companyCode: data.companyCode,
                rememberMe: data.rememberMe,
            }));
            setAuthError('');
            setAuthStage('mfaRequired');
        } else {
            processSuccessfulAuth(data);
        }
    };

    const handleMfaSuccess = tokenData => {
        setAuthError('');
        processSuccessfulAuth(tokenData);
    };

    const handlePasswordResetSuccess = () => {
        Toast.success(getText('msg_e91c04021254'));
        setAuthError('');
        returnToLogin();
    };

    const background = () => css`
        ${themeStyles?.loginBackground?.type === 'color'
            ? `background: ${themeStyles?.loginBackground?.color}`
            : `background-image: url(${themeStyles?.loginBackground?.image})`}
    `;

    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobileDevice = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobileDevice();
        window.addEventListener('resize', checkMobileDevice);
        return () => window.removeEventListener('resize', checkMobileDevice);
    }, []);

    return (
        <div className={styles.container} css={background}>
            <HeightScaledContent baseHeight={900}>
                <div
                    className={`${styles.authBox} col-12 col-sm-12 col-md-7 col-lg-5 m-auto`}
                >
                    <div
                        className={`${styles.errorContainer} ${authError ? '' : 'invisible'}`}
                    >
                        <p className={'text-danger'}>{authError}</p>
                    </div>
                    {!isCompanyCodeReady ||
                    authStage === 'authenticating-user' ? (
                        <div className="text-center">
                            {authStage === 'authenticating-user' && (
                                <p>{getText('msg_e6ed4bd8f37e')}</p>
                            )}
                            <Spinner
                                className={styles.loadingSpinner}
                                width={50}
                            />
                        </div>
                    ) : (
                        <div
                            className={`${!isMobile ? 'ing-card' : ''} ${styles.authContainer} p-4 py-5`}
                        >
                            {authStage === 'initial' && (
                                <LoginForm
                                    onSuccess={handleLoginSuccess}
                                    handleAuthError={handleAuthError}
                                    authData={authData}
                                    onBeforeSubmit={dismissLoggedOutToast}
                                />
                            )}
                            {authStage === 'mfaRequired' && (
                                <MfaForm
                                    onSuccess={handleMfaSuccess}
                                    handleAuthError={handleAuthError}
                                    authData={authData}
                                    onBeforeSubmit={dismissLoggedOutToast}
                                />
                            )}
                            {authStage === 'forgot-password' && (
                                <ForgotPasswordForm
                                    handleAuthError={handleAuthError}
                                    authData={authData}
                                />
                            )}
                            {authStage === 'reset-password' && (
                                <ResetPasswordForm
                                    onSuccess={handlePasswordResetSuccess}
                                    handleAuthError={handleAuthError}
                                    authData={authData}
                                />
                            )}
                        </div>
                    )}
                </div>
            </HeightScaledContent>
        </div>
    );
};

export default AuthPage;
